import { Check, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { trackEvent } from "@/lib/analytics";

const ABOUT_IMG = "https://images.unsplash.com/photo-1642022143908-fe7e3160a56e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const points = [
  "We follow Christ as He taught — not as men later edited.",
  "We keep the whole law of God in love, not legalism.",
  "We call the Body back to obedience and holiness.",
  "We restore what was removed, and remove what was added.",
];

export const About = () => (
  <section id="about" data-testid="about-section" className="bg-[#141311] py-24 md:py-32 border-y border-[#2B2825]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
      <Reveal>
        <div className="relative">
          <div className="absolute -top-4 -left-4 h-full w-full border border-[#4A453F]" />
          <img
            src={ABOUT_IMG}
            alt="An ancient open book"
            className="relative w-full h-[420px] md:h-[560px] object-cover grayscale-[30%]"
          />
        </div>
      </Reveal>

      <div>
        <Reveal>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
            Where We Stand
          </p>
          <h2 className="font-heading font-light tracking-tight text-4xl md:text-6xl leading-tight mb-8">
            A return to the <span className="italic text-[#D4AF37]">unaltered</span> gospel.
          </h2>
          <p className="text-base md:text-lg leading-relaxed font-light text-[#A39E98] mb-10">
            For generations, the commandments of God were traded for the
            traditions of men. The dietary laws, the Sabbath, the Feast Days —
            set aside, softened, or removed entirely. We are a movement of
            believers committed to restoring the faith exactly as Christ
            delivered it.
          </p>
        </Reveal>

        <ul className="space-y-5">
          {points.map((p, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <li data-testid={`about-point-${i}`} className="flex items-start gap-4">
                <span className="flex-shrink-0 mt-1 flex h-6 w-6 items-center justify-center border border-[#D4AF37]">
                  <Check strokeWidth={1.5} className="h-4 w-4 text-[#D4AF37]" />
                </span>
                <span className="text-base font-light text-[#F4F0EA]">{p}</span>
              </li>
            </Reveal>
          ))}
        </ul>
      </div>
    </div>

    <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-20 md:mt-28">
      <Reveal>
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
          Now Streaming
        </p>
        <h3 className="font-heading font-light tracking-tight text-3xl md:text-4xl leading-tight mb-8 text-[#F4F0EA]">
          Restoration Podcast <span className="italic text-[#D4AF37]">with the Elders</span>
        </h3>
      </Reveal>
      <Reveal delay={0.1}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
          <div className="border border-[#2B2825] bg-black p-2 md:p-3 flex items-center">
            <video
              data-testid="about-podcast-video"
              className="w-full max-h-[70vh] object-contain bg-black"
              src="https://customer-assets.emergentagent.com/job_sabbath-way/artifacts/zhfkkpvh_Streaming%20now%21%20Restoration%20Podcast%20with%20the%20Elders.%20%23apostolic%20%23messianic%20%23podcast%20%23podcastclips.mp4"
              controls
              playsInline
              preload="metadata"
            />
          </div>

          <a
            href="https://www.restorationpodcast.com"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-podcast-link"
            onClick={() => trackEvent("click_external_link", { label: "Restoration Podcast", link_url: "https://www.restorationpodcast.com" })}
            className="group relative block overflow-hidden border border-[#2B2825]"
          >
            <img
              src="/podcast-studio.jpg"
              alt="Restoration Podcast with the Elders"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/20 to-transparent" />
            <div className="absolute inset-0 bg-[#0A0A0A]/10 group-hover:bg-[#0A0A0A]/0 transition-colors" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#D4AF37] mb-2">
                Visit the Podcast
              </p>
              <div className="flex items-center gap-2 text-[#F4F0EA] font-heading text-2xl md:text-3xl font-light">
                www.restorationpodcast.com
                <ArrowUpRight
                  strokeWidth={1.5}
                  className="h-6 w-6 text-[#D4AF37] transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </div>
            </div>
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);
